import React, { ReactNode, useEffect, useState } from "react";
import clsx from "clsx";

import ImageIcon from "@material-ui/icons/Image";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH, EMPTY_STRING } from "common/constants";
import Breakpoints from "common/breakpoints";
import Toggle from "components/Toggle";
import ConditionNotes from "./ConditionNotes";
import {
  EVariantExamsHistory,
  getCodedString,
  getVariantsClassNames
} from "../helper";

import useStyles from "./styles";

interface IEmptyEntry {
  id: string;
}

interface IProps<T = IEmptyEntry, S = IEmptyEntry> {
  name: string;
  value: ReactNode;
  data: T[];
  variant?: EVariantExamsHistory;
  accordionTitle?: string;
  baseFields?: (keyof T)[];
  subFields?: (keyof S)[];
  subEntriesFieldName?: string;
  className?: string;
}

const MAX_PREVIEW_ITEMS_AMOUNT = 5;

const getDivider = (
  defaultDivider: string,
  withSpaces: boolean,
  spacedDivider?: string
): string => {
  const expandedDivider = spacedDivider || ` ${defaultDivider} `;

  return withSpaces ? expandedDivider : defaultDivider;
};

const NestedMultipleBlock = <T, S = IEmptyEntry>(props: IProps<T, S>) => {
  const {
    name,
    value,
    data,
    variant,
    accordionTitle,
    baseFields,
    subFields,
    subEntriesFieldName,
    className
  } = props;

  const classes = useStyles();
  const theme = useTheme();
  const matchesDownSX720 = useMediaQuery(
    theme.breakpoints.up(Breakpoints.SX_720)
  );

  const { detailsClassName } = getVariantsClassNames(variant);
  const isHorseDetailsView = variant === EVariantExamsHistory.DETAILS;

  const [isToggleOn, setIsToggleOn] = useState(false);

  const shouldDisplayToggleInColumn = [
    !isHorseDetailsView,
    matchesDownSX720
  ].every(Boolean);

  const getEntireCodedString = (item: T): string => {
    const doubleSlashDivider = getDivider("//", isToggleOn);
    const singleSlashDivider = getDivider("/", isToggleOn);
    const commaDivider = getDivider(",", isToggleOn, ", ");

    const basedFieldsCodedString = getCodedString<T>(
      item,
      baseFields,
      isToggleOn,
      doubleSlashDivider
    );
    const emptyEntryRegExp = /^[,/-]+$/g;
    const shouldDisplaySubEntriesCodedString = [
      item,
      item?.[subEntriesFieldName]?.length,
      subFields,
      subFields?.length
    ].every(Boolean);
    let subFieldsCodedString = EMPTY_STRING;

    if (shouldDisplaySubEntriesCodedString) {
      subFieldsCodedString = `${doubleSlashDivider}${item[subEntriesFieldName]
        .map(subEntry =>
          getCodedString<S>(subEntry, subFields, isToggleOn, commaDivider)
        )
        .join(singleSlashDivider)}`;
    }
    const resultString = `${basedFieldsCodedString}${subFieldsCodedString}`;

    return emptyEntryRegExp.test(resultString) ? DASH : resultString;
  };

  const handleToggleChange = () => {
    setIsToggleOn(!isToggleOn);
  };

  const renderItem = (item?: T, index?: number) => {
    let shouldShowConditionNoteSection: boolean;

    if (item?.["observationEntries"]) {
      shouldShowConditionNoteSection = item["observationEntries"].some(
        (obs: racehorse360.HorseExamDetailObservationEntry) => obs.notes
      );
    }

    return (
      <React.Fragment key={index}>
        <div className={clsx(classes.row, classes.subRow, detailsClassName)}>
          <div>
            <div className={classes.rowMainItem}>
              <span className={classes.codedString}>
                {getEntireCodedString(item)}
              </span>
              <ImageIcon
                className={clsx(classes.imageIcon, detailsClassName)}
              />
            </div>

            {shouldShowConditionNoteSection && (
              <ConditionNotes entries={item["observationEntries"]} />
            )}
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderContent = () => {
    if (isHorseDetailsView && data.length > MAX_PREVIEW_ITEMS_AMOUNT) {
      const previewData = data.filter(
        (item, index) => index < MAX_PREVIEW_ITEMS_AMOUNT
      );
      const restData = data.filter(
        (item, index) => index >= MAX_PREVIEW_ITEMS_AMOUNT
      );

      return (
        <>
          {previewData.map(renderItem)}

          <Accordion className={classes.accordion}>
            <AccordionSummary
              className={classes.accordionSummary}
              classes={{
                content: classes.accordionSummaryContent,
                expandIcon: classes.expandIcon
              }}
              expandIcon={<ExpandMoreIcon />}
            >
              {`${accordionTitle} (${data.length})`}
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.accordionDetails }}>
              {restData.map(renderItem)}
            </AccordionDetails>
          </Accordion>
        </>
      );
    }

    return data.length ? data.map(renderItem) : renderItem();
  };

  useEffect(() => {
    setIsToggleOn(false);
  }, [data]);

  return (
    <div className={clsx(classes.root, detailsClassName, className)}>
      <div className={clsx(classes.row, classes.mainRow, detailsClassName)}>
        <div>
          <span className={classes.rowName}>{name}</span>
          {shouldDisplayToggleInColumn && (
            <Toggle
              className={clsx(classes.toggle, {
                horseDetailsView: isHorseDetailsView
              })}
              checked={isToggleOn}
              title={"Expand Info"}
              disabled={!data.length}
              onSwitchToggle={handleToggleChange}
            />
          )}
        </div>

        <div className={classes.rowValueWrapper}>
          {!shouldDisplayToggleInColumn && (
            <Toggle
              className={clsx(classes.toggle, {
                horseDetailsView: isHorseDetailsView
              })}
              checked={isToggleOn}
              title={"Expand Info"}
              disabled={!data.length}
              onSwitchToggle={handleToggleChange}
            />
          )}
          {value}
        </div>
      </div>
      {renderContent()}
    </div>
  );
};

export default NestedMultipleBlock;
